/**
 * Pojmenované skupiny nodů (karta Uzly / infrastruktura).
 * localStorage + volitelný sync přes ui.prefs (stejný vzor jako start-menu).
 */

export interface NodeGroupDef {
  id: string;
  name: string;
}

export interface NodeGroupsState {
  groups: NodeGroupDef[];
  /** node route name → group id */
  membership: Record<string, string>;
}

const LS_KEY = 'acm_node_groups';
const PREFS_KEY = 'node_groups';

function empty(): NodeGroupsState {
  return { groups: [], membership: {} };
}

export function loadNodeGroups(): NodeGroupsState {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return empty();
    const p = JSON.parse(raw) as NodeGroupsState;
    if (!Array.isArray(p.groups) || typeof p.membership !== 'object' || !p.membership) {
      return empty();
    }
    return {
      groups: p.groups
        .filter((g) => g && typeof g.id === 'string' && typeof g.name === 'string')
        .map((g) => ({ id: g.id, name: g.name.trim() || g.id })),
      membership: { ...p.membership },
    };
  } catch {
    return empty();
  }
}

export function saveNodeGroups(state: NodeGroupsState): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(state));
  } catch {
    /* private mode */
  }
}

export function createNodeGroup(name: string, state?: NodeGroupsState): NodeGroupsState {
  const base = state ?? loadNodeGroups();
  const n = name.trim();
  if (!n) return base;
  const id = `grp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
  const next = {
    groups: [...base.groups, { id, name: n }],
    membership: { ...base.membership },
  };
  saveNodeGroups(next);
  return next;
}

export function renameNodeGroup(
  id: string,
  name: string,
  state?: NodeGroupsState,
): NodeGroupsState {
  const base = state ?? loadNodeGroups();
  const n = name.trim();
  if (!n) return base;
  const next = {
    groups: base.groups.map((g) => (g.id === id ? { ...g, name: n } : g)),
    membership: { ...base.membership },
  };
  saveNodeGroups(next);
  return next;
}

export function deleteNodeGroup(id: string, state?: NodeGroupsState): NodeGroupsState {
  const base = state ?? loadNodeGroups();
  const membership = { ...base.membership };
  for (const [node, gid] of Object.entries(membership)) {
    if (gid === id) delete membership[node];
  }
  const next = {
    groups: base.groups.filter((g) => g.id !== id),
    membership,
  };
  saveNodeGroups(next);
  return next;
}

/** groupId null = odebrat ze skupiny */
export function assignNodeToGroup(
  nodeName: string,
  groupId: string | null,
  state?: NodeGroupsState,
): NodeGroupsState {
  const base = state ?? loadNodeGroups();
  const membership = { ...base.membership };
  if (!groupId) delete membership[nodeName];
  else membership[nodeName] = groupId;
  const next = { groups: base.groups, membership };
  saveNodeGroups(next);
  return next;
}

export { PREFS_KEY as NODE_GROUPS_PREFS_KEY };
